import {CommonModule} from '@angular/common';
import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {TranslateModule} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {
    MATHIA_SEARCH_FILTER_CONFIG,
    MathiaFacetScope,
    MathiaSearchFilterConfig,
} from '../mathia-search-filter-config';
import {
    SearchEngineModule,
    SearchFilterDefinition,
    SearchFilterSelection,
    SearchFilterSelectionValue,
    SearchFilterValue,
    isSearchFilterSelectionArray,
} from 'shared/search-engine';
import {MathiaSearchDataService} from '../mathia-search-data.service';

@Component({
    selector: 'app-mathia-lessons-search-filter',
    standalone: true,
    imports: [CommonModule, SearchEngineModule, TranslateModule],
    templateUrl: './mathia-lessons-search-filter.component.html',
    styleUrls: ['./mathia-lessons-search-filter.component.scss'],
})
export class MathiaLessonsSearchFilterComponent implements OnChanges, OnInit, OnDestroy {
    @Input() public definitions: readonly SearchFilterDefinition<SearchFilterValue>[] = [];
    @Input() public initialSelection: SearchFilterSelection<SearchFilterValue> = {};
    @Input() public filterConfig: readonly MathiaSearchFilterConfig[] = MATHIA_SEARCH_FILTER_CONFIG;
    @Input() public facetScope: MathiaFacetScope = 'activity';
    @Input() public resultCount: number | null = null;
    @Output() public selectionChange = new EventEmitter<SearchFilterSelection<SearchFilterValue>>();

    public selection: SearchFilterSelection<SearchFilterValue> = {};
    public isLoading = true;

    private readonly searchData = inject(MathiaSearchDataService);
    private definitionsSubscription?: Subscription;
    private initialDefinitions: readonly SearchFilterDefinition<SearchFilterValue>[] = [];
    private requestVersion = 0;

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialSelection'] && !changes['initialSelection'].firstChange && this.definitions.length > 0) {
            this.cancelFacetRequest();
            this.selection = this.normalizeSelection(this.initialSelection);
        }
    }

    public ngOnInit(): void {
        if (this.definitions.length > 0) {
            this.initialDefinitions = [...this.definitions];
            this.selection = this.normalizeSelection(this.initialSelection);
            this.isLoading = false;
            return;
        }

        this.loadInitialDefinitions();
    }

    public ngOnDestroy(): void {
        this.definitionsSubscription?.unsubscribe();
    }

    public onSelectionChange(selection: SearchFilterSelection<SearchFilterValue>): void {
        const changedIndex = this.findChangedDefinitionIndex(selection);
        if (changedIndex === -1) {
            return;
        }

        const nextSelection = this.resetFollowingFilters(selection, changedIndex);
        this.selection = nextSelection;
        this.selectionChange.emit({...nextSelection});
        this.cancelFacetRequest();

        const changedDefinition = this.definitions[changedIndex];
        if (!changedDefinition || changedIndex >= this.definitions.length - 1) {
            return;
        }

        const changedConfig = this.getFilterConfig(changedDefinition.key);
        if (changedConfig?.independent === true) {
            return;
        }

        if (!changedConfig?.facetKey) {
            this.restoreFollowingDefinitions(changedIndex);
            return;
        }

        this.markFollowingFiltersAsLoading(changedIndex);
        this.loadFollowingDefinitions(nextSelection, changedIndex);
    }

    private loadInitialDefinitions(): void {
        this.cancelFacetRequest();
        const requestVersion = ++this.requestVersion;
        this.isLoading = true;
        this.definitionsSubscription = this.searchData.getFilterDefinitions({}, this.filterConfig, this.facetScope).subscribe({
            next: definitions => {
                if (requestVersion !== this.requestVersion) {
                    return;
                }

                this.definitions = definitions;
                this.initialDefinitions = definitions;
                this.selection = this.normalizeSelection(this.initialSelection);
                this.isLoading = false;
            },
            error: () => {
                if (requestVersion === this.requestVersion) {
                    this.definitions = [];
                    this.isLoading = false;
                }
            },
        });
    }

    private loadFollowingDefinitions(
        selection: SearchFilterSelection<SearchFilterValue>,
        changedIndex: number,
    ): void {
        const requestVersion = ++this.requestVersion;
        this.definitionsSubscription?.unsubscribe();
        this.definitionsSubscription = this.searchData.getFilterDefinitions(selection, this.filterConfig, this.facetScope).subscribe({
            next: definitions => {
                if (requestVersion !== this.requestVersion) {
                    return;
                }

                this.definitions = this.definitions.map((definition, index) => index > changedIndex
                    && this.getFilterConfig(definition.key)?.independent !== true
                    ? {...(definitions[index] ?? definition), loading: false}
                    : definition);
            },
            error: () => {
                if (requestVersion !== this.requestVersion) {
                    return;
                }

                this.definitions = this.definitions.map((definition, index) => index > changedIndex
                    && this.getFilterConfig(definition.key)?.independent !== true
                    ? {...definition, options: [], enabled: false, loading: false}
                    : definition);
            },
        });
    }

    private markFollowingFiltersAsLoading(changedIndex: number): void {
        this.definitions = this.definitions.map((definition, index) => index > changedIndex
            && this.getFilterConfig(definition.key)?.independent !== true
            ? {...definition, options: [], enabled: false, loading: true}
            : definition);
    }

    private restoreFollowingDefinitions(changedIndex: number): void {
        this.definitions = this.definitions.map((definition, index) => index > changedIndex
            && this.getFilterConfig(definition.key)?.independent !== true
            ? this.initialDefinitions[index] ?? definition
            : definition);
    }

    private cancelFacetRequest(): void {
        this.requestVersion++;
        this.definitionsSubscription?.unsubscribe();
        this.definitionsSubscription = undefined;
    }

    private findChangedDefinitionIndex(selection: SearchFilterSelection<SearchFilterValue>): number {
        return this.definitions.findIndex(definition => !this.sameValue(
            this.selection[definition.key],
            selection[definition.key],
        ));
    }

    private resetFollowingFilters(
        selection: SearchFilterSelection<SearchFilterValue>,
        changedIndex: number,
    ): SearchFilterSelection<SearchFilterValue> {
        const nextSelection: SearchFilterSelection<SearchFilterValue> = {
            ...this.normalizeSelection(selection),
        };
        const changedDefinition = this.definitions[changedIndex];
        const changedConfig = this.getFilterConfig(changedDefinition.key);

        for (let index = changedIndex + 1; index < this.definitions.length; index++) {
            const definition = this.definitions[index];
            if (changedConfig?.independent !== true && this.getFilterConfig(definition.key)?.independent !== true) {
                delete nextSelection[definition.key];
            }
        }

        const changedValue = nextSelection[changedDefinition.key];
        if (changedValue === null || changedValue === undefined || (isSearchFilterSelectionArray(changedValue) && changedValue.length === 0)) {
            delete nextSelection[changedDefinition.key];
        }

        return nextSelection;
    }

    private normalizeSelection(
        selection: SearchFilterSelection<SearchFilterValue>,
    ): SearchFilterSelection<SearchFilterValue> {
        return Object.fromEntries(
            Object.entries(selection)
                .filter(([key]) => this.definitions.some(definition => definition.key === key))
                .map(([key, value]) => {
                    const definition = this.definitions.find(item => item.key === key);
                    if (definition?.control === 'checkbox') {
                        return [key, this.toBoolean(value)];
                    }

                    return [key, definition?.multiple ? this.toArray(value) : value];
                }),
        );
    }

    private getFilterConfig(key: string): MathiaSearchFilterConfig | undefined {
        return this.filterConfig.find(filterConfig => filterConfig.key === key);
    }

    private toBoolean(value: SearchFilterSelectionValue<SearchFilterValue>): boolean {
        return value === true || value === 1 || value === 'true' || value === '1';
    }

    private toArray(value: SearchFilterSelectionValue<SearchFilterValue>): readonly SearchFilterValue[] {
        if (value === null || value === undefined) {
            return [];
        }

        if (isSearchFilterSelectionArray(value)) {
            return value;
        }

        return typeof value === 'string' && value.includes(',')
            ? value.split(',').filter(item => item !== '')
            : [value];
    }

    private sameValue(
        left: SearchFilterSelectionValue<SearchFilterValue>,
        right: SearchFilterSelectionValue<SearchFilterValue>,
    ): boolean {
        return this.toComparableValue(left) === this.toComparableValue(right);
    }

    private toComparableValue(value: SearchFilterSelectionValue<SearchFilterValue>): string {
        if (value === null || value === undefined) {
            return '';
        }

        if (isSearchFilterSelectionArray(value)) {
            return value.map(item => String(item)).sort().join(',');
        }

        return String(value);
    }
}
